
import { graphql, type ResultOf } from '@client/shared/graphql';
import { gqlRequest } from '@client/shared/graphql';
import { viewerNextBooking, type ViewerNextBooking } from './viewer-next-booking.ts';

export const ListingsQuery = graphql(`
  query Listings($bookableOnly: Boolean, $includeArchived: Boolean) {
    listings(bookableOnly: $bookableOnly, includeArchived: $includeArchived) {
      id
      formattedAddress
      addressLine1
      addressLine2
      city
      region
      postalCode
      bookingHorizonDays
      bookingUntil
      viewerCanBook
      archivedAt
      bookings {
        agentUserId
        startsAt
        endsAt
      }
    }
  }
`);

export type ListingsData = ResultOf<typeof ListingsQuery>;

export function listingsQueryKey(bookableOnly: boolean, includeArchived: boolean) {
  return ['listings', 'browse', { bookableOnly, includeArchived }] as const;
}

export const LISTINGS_QUERY_PREFIX = ['listings', 'browse'] as const;

export function fetchListings(
  bookableOnly: boolean,
  includeArchived: boolean,
  signal?: AbortSignal,
): Promise<ListingsData> {
  return gqlRequest(ListingsQuery, { bookableOnly, includeArchived }, { signal });
}

export interface ListingCard {
  readonly id: string;
  readonly address: string;
  readonly locality: string | null;
  readonly bookingHorizonDays: number | null;
  readonly bookingUntil: string | null;
  readonly canBook: boolean;
  readonly nextBooking: ViewerNextBooking | null;
  readonly isViewerNextBooking: boolean;
  readonly archived: boolean;
}

type ListingRow = NonNullable<NonNullable<ListingsData['listings']>[number]>;

export function displayAddress(listing: ListingRow): string {
  if (listing.formattedAddress) return listing.formattedAddress;
  const parts = [listing.addressLine1, listing.addressLine2].filter(
    (p): p is string => Boolean(p),
  );
  return parts.length > 0 ? parts.join(', ') : 'Untitled listing';
}

export function locality(listing: ListingRow): string | null {
  const parts = [listing.city, listing.region].filter((p): p is string => Boolean(p));
  return parts.length > 0 ? parts.join(', ') : null;
}

export function toListingCards(
  data: ListingsData,
  viewerId: string | null,
  now: Date | number,
): ListingCard[] {
  const listings = data.listings ?? [];

  const entries = listings
    .filter((l): l is NonNullable<typeof l> & { id: string } => Boolean(l?.id))
    .map((l) => ({ l, nextBooking: viewerNextBooking(l.bookings ?? [], viewerId, now) }));

  let globalMinMs = Infinity;
  for (const { nextBooking } of entries) {
    if (nextBooking) {
      const ms = Date.parse(nextBooking.startsAt);
      if (ms < globalMinMs) globalMinMs = ms;
    }
  }

  return entries.map(({ l, nextBooking }) => ({
    id: l.id,
    address: displayAddress(l),
    locality: locality(l),
    bookingHorizonDays: l.bookingHorizonDays ?? null,
    bookingUntil: l.bookingUntil ?? null,
    canBook: l.viewerCanBook ?? true,
    nextBooking,
    isViewerNextBooking: nextBooking != null && Date.parse(nextBooking.startsAt) === globalMinMs,
    archived: l.archivedAt != null,
  }));
}
